import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { FormDrawer } from "@/app/FormDrawer";
import { accounts, channels, channelGroups } from "@/app/master";
import { nameOf, useList } from "@/app/queries";
import { ROUTING_INVALIDATE, routingRules, type RoutingReferences, type RoutingRule, type RoutingState, type RoutingTemplate } from "@/app/routing";
import { useEntityForm } from "@/app/useEntityForm";
import { Alert, Button, Modal } from "@/ui";
import { RoutingEditLoader } from "./RoutingEditLoader";
import { RoutingRuleForm } from "./RoutingRuleForm";
import { routingRuleDraft, routingRuleInput } from "./routingRuleDraft";

/** 编辑前读取一致的对象详情与引用，引用失败不允许共享修改或删除。 */
export function RoutingRuleEditor({ row, state, templates, onClose }: {
  row: RoutingRule | null; state?: RoutingState; templates: RoutingTemplate[]; onClose: () => void;
}) {
  return <RoutingEditLoader row={row} queryKey="routing-rules" noun="规则" resource={routingRules} onClose={onClose}>{(loaded, references) =>
    <RuleEditForm row={loaded} state={state} references={references} templates={templates} onClose={onClose} />
  }</RoutingEditLoader>;
}

function RuleEditForm({ row, state, references, templates, onClose }: {
  row: RoutingRule | null; state?: RoutingState; references: RoutingReferences; templates: RoutingTemplate[]; onClose: () => void;
}) {
  const canWrite = useCanWrite();
  const [confirm, setConfirm] = useState(false);
  const form = useEntityForm(routingRuleDraft(row ?? undefined), routingRuleInput, body => row ? routingRules.update(row.id, body) : routingRules.create(body), {
    invalidate: ROUTING_INVALIDATE, message: "已保存路由规则，影响后续新首发", onSaved: onClose,
  });
  const groupList = useList("channel-groups", channelGroups);
  const channelList = useList("channels", channels);
  const accountList = useList("accounts", accounts);
  const shared = references.account_ids.length > 0 || references.template_ids.length > 0;
  const ready = state?.mode === "active";
  const submit = () => {
    if (!ready || !canWrite || form.warning || form.saving) return;
    if (shared && routingRuleInput(form.draft).body) setConfirm(true); else form.submit();
  };
  return <>
    <FormDrawer open title={row ? `${canWrite ? "编辑" : "查看"}规则 ${row.name}` : "新建独立规则"} saving={form.saving || !ready} error={form.error} warning={form.warning} onSubmit={submit} onClose={onClose}>
      {!ready && <Alert tone="danger" title="路由状态读取失败">草稿保留，请关闭后刷新并重新打开；不会自动覆盖或采用新版本重试。</Alert>}
      <Alert tone="neutral" title="共享修改的影响范围">账号：{references.account_ids.map(id => `${nameOf(accountList.data, id)} (#${id})`).join("、") || "无"}；模板：{references.template_ids.map(id => `${nameOf(templates, id)} (#${id})`).join("、") || "无"}。包含停用、过期绑定，以及绑定了含此规则模板的账号。优先级属于账号或模板绑定。</Alert>
      {(groupList.error || channelList.error || accountList.error) && <Alert tone="danger" title="资源名称加载失败">{groupList.error?.message ?? channelList.error?.message ?? accountList.error?.message}，未加载名称时以 ID 识别引用。</Alert>}
      <fieldset className="ax-fieldset" disabled={!canWrite || !ready || form.saving || !!form.warning}>
        <RoutingRuleForm disabled={!canWrite || !ready || form.saving || !!form.warning} draft={form.draft} errors={form.errors} set={form.set} groups={groupList.data ?? []} channels={channelList.data ?? []} />
      </fieldset>
    </FormDrawer>
    <Modal open={confirm} title="确认修改共享规则" onClose={() => setConfirm(false)} footer={<><Button onClick={() => setConfirm(false)}>取消</Button><Button variant="primary" disabled={!ready || form.saving} onClick={() => { setConfirm(false); if (ready) form.submit(); }}>确认共享修改</Button></>}>
      <p>此操作将影响 {references.account_ids.length} 个账号、{references.template_ids.length} 个模板引用者。保存后影响后续新首发；请确认这次调整应作用于所有引用者。</p>
    </Modal>
  </>;
}
